
import { Component, Vue, Prop } from 'vue-property-decorator';
import { DeviceInfo, SharedDeviceInfo, getDefaultShareInfo } from '@/types/device-info';
import { Mutation } from 'vuex-class';
import { mutations } from '@/store/types';
import { SetDeviceMutationType, SetDevicesMutationType } from '@/types/store/mutations';
import ShareEditPopup from '@/components/devices/popups/ShareEditPopup.vue';
import api from '@/modules/api';
import { PRIVILEGES_OWNER } from '@/utils/constants';
import * as dlg from '@/modules/dlg';
import { Action, State } from 'vuex-class';
import { LoadDevicesActionType, LoadDeviceActionType } from '@/types/store/actions';
import { actions } from '../../../store/types';
import { UserInfo } from '../../../types/user-info';
import { DeviceType } from '@/types/device-info';
import { getErrorMessage } from '@/utils/common';
import { validatePrivilege } from '@/utils/devices';
import { DevicePrivileges } from '@/constants/device_privileges';
import { userRoles } from '@/constants/userRoles';

@Component({
    components: {
        ShareEditPopup,
    },
})
export default class DeviceOptionsMenu extends Vue {
    protected shares: SharedDeviceInfo[] = [];
    protected share: SharedDeviceInfo | null = null;
    protected ManageSharingTitle = 'Manage sharing';
    protected showFullForm = false;
    protected tableLoading = false;
    protected validatePrivilege = validatePrivilege;
    protected DevicePrivileges = DevicePrivileges;

    @Action(actions.LOAD_DEVICES)
    loadDevices!: LoadDevicesActionType;

    @Action(actions.LOAD_DEVICE)
    loadDevice!: LoadDeviceActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    @Mutation(mutations.SET_DEVICES)
    setDevices!: SetDevicesMutationType;

    @State('user')
    user!: UserInfo;

    @State('device')
    current!: DeviceInfo;

    @State('devices')
    devices!: DeviceInfo[];

    @Prop({
        default() {
            return [];
        },
    })
    hideOptions!: string[];

    @Prop({ required: true })
    device!: DeviceInfo;

    @Prop({ required: false })
    optionClasses!: string;

    validateAccess(privileges: number[] = []) {
        return this.validatePrivilege(this.device.privilege, privileges);
    }

    validateRestricted() {
        return this.validateAccess([DevicePrivileges.SuperOwner, DevicePrivileges.Owner]);
    }

    async alarm() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.updateDeviceAlarm(
                this.device.deviceUUID,
                this.device.alarm === true ? 'off' : 'on',
            );

            const updatedDevices = this.devices.map((item) => {
                if (item.deviceUUID === this.device.deviceUUID) return { ...item, alarm: !this.device.alarm };
                return item;
            });
            this.setDevices({ devices: updatedDevices });
            if (this.device._id == this.current._id) {
                this.setCurrentDevice({ device: { ...this.current, alarm: !this.device.alarm } });
            }

            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    gotoSettings() {
        this.setCurrentDevice({ device: this.device });
        this.$router.push({ name: 'device-settings', params: { deviceUUID: this.device.deviceUUID as string } });
    }

    gotoSharings() {
        this.setCurrentDevice({ device: this.device });
        this.$router.push({
            name: 'device-settings-sharing',
            params: { deviceUUID: this.device.deviceUUID as string, tab: 'sharing' },
        });
    }

    async remove() {
        if (this.device.deviceType === DeviceType.AirboltGps && !(this.device?.subscription?.status === 'Inactive'))
            return dlg.alertInfo(
                `Device has an active subscription, please cancel the subscription to delete the device.`,
            );

        const confirmed = await dlg.confirm(
            'Are you sure you want to delete the device from your account? Deleting the device will remove it from account.',
            'Device removal',
        );

        if (!confirmed) return;

        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            let deviceId = this.device._id;
            await api.removeDevice(deviceId as string);
            await this.loadDevices({ userId: this.user._id as string });
            if (this.current && this.current._id === deviceId) {
                this.$store.commit(mutations.SET_CURRENT_DEVICE, { device: null });
            }
            Vue.toasted.success(`Removed successfully`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    has(opt: string) {
        return !this.hideOptions.includes(opt);
    }

    notSharedUser() {
        return !this.user?.roles.includes(userRoles.SharedUser);
    }

    shareDevice() {
        this.ManageSharingTitle = 'Share';
        this.showFullForm = true;
        this.add();
    }

    async manageShareDevice() {
        this.ManageSharingTitle = 'Manage sharing';
        this.showFullForm = false;
        this.add();
        this.getSelectedShares();
    }
    async getSelectedShares() {
        this.shares = [];
        if (this.device.shares != undefined) {
            await this.fetchSharesList(); // this.shares = this.device.shares;
        } else if (this.device.share_count && this.device.share_count > 0) {
            await this.fetchSharesList();
        }
    }
    async fetchSharesList() {
        this.tableLoading = true;
        try {
            this.shares = await api.loadShare(this.device.deviceUUID);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error, `Something went wrong while fetching shared with`));
        } finally {
            this.tableLoading = false;
        }
    }

    async add() {
        this.share = getDefaultShareInfo({
            deviceUUID: this.device.deviceUUID,
        });
    }

    async refresh() {
        await this.loadDevices({ userId: this.user._id as string });
        if (this.current && this.current._id) {
            await this.loadDevice({ deviceUUID: this.current.deviceUUID });
        }
    }

    async fetchModemInfo() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.getModemInfo(this.device.deviceUUID);
            Vue.toasted.success(`Requested`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error, `Something went wrong fetching temperature.`));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async save(share: SharedDeviceInfo) {
        this.$store.commit(mutations.LOADING, { loading: true });
        if (share._id) {
            try {
                await api.updateShare({
                    ...share,
                    isOwnerFlag: share.accessRightsFlag === PRIVILEGES_OWNER ? 1 : 0,
                });
                await this.refresh();
                this.share = null;
                Vue.toasted.success('Updated successfully');
            } catch (error) {
                Vue.toasted.error(getErrorMessage(error));
            } finally {
                this.$store.commit(mutations.LOADING, { loading: false });
            }
        } else {
            try {
                await api.createShare({
                    ...share,
                    masterKey: this.device.passcode,
                    isOwnerFlag: share.accessRightsFlag === PRIVILEGES_OWNER ? 1 : 0,
                });
                await this.refresh();
                this.share = null;
                Vue.toasted.success('Shared successfully');
            } catch (error) {
                Vue.toasted.error(getErrorMessage(error));
            } finally {
                this.$store.commit(mutations.LOADING, { loading: false });
            }
        }
    }
    async removeListShare(share: SharedDeviceInfo) {
        this.PerformRemoveShare(share, true);
    }
    async removeShare(share: SharedDeviceInfo) {
        this.PerformRemoveShare(share, false);
    }
    async PerformRemoveShare(share: SharedDeviceInfo, isFromList: boolean) {
        if (!share._id) {
            return;
        }
        const confirmed = await dlg.confirm('Are you sure you want to delete share?', 'Share removal');
        if (!confirmed) {
            return;
        }
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.deleteShare(share._id);
            await this.refresh();
            if (isFromList) {
                this.getSelectedShares();
            } else {
                this.share = null;
            }
            Vue.toasted.success('Removed successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
    async resend(share: SharedDeviceInfo) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.resendShare(share);
            // this.share = null;
            Vue.toasted.success('Shared successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
    handleListEdit(share: SharedDeviceInfo) {
        this.showFullForm = true;
        this.share = { ...share };
    }
    async removeAllShare() {
        const confirmed = await dlg.confirm(
            'Are you sure you want to remove all sharing of device?',
            'Device sharing removal',
        );

        if (!confirmed) {
            return;
        }
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            let deviceUUID = this.device.deviceUUID;
            await api.removeAllShareOfDevice(deviceUUID as string);
            await this.loadDevices({ userId: this.user._id as string });
            await this.loadDevice({ deviceUUID: this.current.deviceUUID as string });
            Vue.toasted.success(`Removed successfully`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
}
